import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { api } from '@/api';
import variationOrdersApi from '@/api/variationOrders';
import { 
  Plus, Search, Filter, FileText, Clock, AlertCircle, 
  Building2, ArrowRight, RefreshCw, X, TrendingUp, TrendingDown,
  CheckCircle, XCircle, Loader2, Calendar, DollarSign, BarChart3,
  PieChart, AlertTriangle, Timer, ArrowUpRight, List, LayoutDashboard,
  FileSpreadsheet, Download
} from 'lucide-react';

// Status badge colors
const getStatusBadge = (status) => {
  const styles = {
    'Draft': 'bg-slate-100 text-slate-700 border-slate-200',
    'RFQ Issued': 'bg-blue-100 text-blue-700 border-blue-200',
    'Quote Received': 'bg-indigo-100 text-indigo-700 border-indigo-200',
    'Under Review': 'bg-amber-100 text-amber-700 border-amber-200',
    'Clarification': 'bg-orange-100 text-orange-700 border-orange-200',
    'Negotiation': 'bg-yellow-100 text-yellow-700 border-yellow-200',
    'Recommended': 'bg-cyan-100 text-cyan-700 border-cyan-200',
    'Technically Approved': 'bg-teal-100 text-teal-700 border-teal-200',
    'Commercially Approved': 'bg-emerald-100 text-emerald-700 border-emerald-200',
    'VO Issued': 'bg-green-100 text-green-700 border-green-200',
    'In Progress': 'bg-purple-100 text-purple-700 border-purple-200',
    'Implemented': 'bg-violet-100 text-violet-700 border-violet-200',
    'Valued/Assessed': 'bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200',
    'Certified': 'bg-pink-100 text-pink-700 border-pink-200',
    'Paid': 'bg-lime-100 text-lime-700 border-lime-200',
    'Closed': 'bg-gray-100 text-gray-700 border-gray-200',
    'Rejected': 'bg-red-100 text-red-700 border-red-200',
    'Disputed': 'bg-rose-100 text-rose-700 border-rose-200',
    'On Hold': 'bg-zinc-100 text-zinc-700 border-zinc-200',
    'Cancelled': 'bg-neutral-100 text-neutral-700 border-neutral-200',
  };
  return styles[status] || 'bg-slate-100 text-slate-700 border-slate-200';
};

// Priority badge colors
const getPriorityBadge = (priority) => {
  const styles = {
    'Normal': 'bg-slate-50 text-slate-600',
    'Urgent': 'bg-amber-50 text-amber-600',
    'Emergency': 'bg-red-50 text-red-600',
  };
  return styles[priority] || 'bg-slate-50 text-slate-600';
};

// Format currency
const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return '-';
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

export default function VariationOrders() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { projectId: projectIdFromRoute } = useParams();
  const projectIdFromUrl = projectIdFromRoute || searchParams.get('project');
  const isProjectSpecific = !!projectIdFromRoute;
  
  // State
  const [variationOrders, setVariationOrders] = useState([]);
  const [projects, setProjects] = useState([]);
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [enums, setEnums] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [activeTab, setActiveTab] = useState('register');
  
  // Filters
  const [search, setSearch] = useState('');
  const [projectFilter, setProjectFilter] = useState(projectIdFromUrl || 'all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 20;
  
  // New VO form
  const [newVO, setNewVO] = useState({
    project_id: projectIdFromUrl || '',
    title: '',
    description: '',
    change_category: '',
    contract_type: 'JBCC',
    priority: 'Normal',
    disciplines: [],
    reason: '',
    proceed_at_risk: false,
  });

  // Load enums
  useEffect(() => {
    const loadEnums = async () => {
      try {
        const res = await variationOrdersApi.getEnums();
        setEnums(res.data);
      } catch (error) {
        console.error('Failed to load enums:', error);
      }
    };
    loadEnums();
  }, []);

  // Load projects
  useEffect(() => {
    const loadProjects = async () => {
      try {
        const res = await api.projects.getAll();
        setProjects(res.data || []);
      } catch (error) {
        console.error('Failed to load projects:', error);
      }
    };
    loadProjects();
  }, []);

  // Load VOs
  const loadVariationOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        page_size: pageSize,
        sort_by: 'created_at',
        sort_order: 'desc',
      };
      
      if (projectFilter && projectFilter !== 'all') {
        params.project_id = projectFilter;
      }
      if (statusFilter && statusFilter !== 'all') {
        params.status = statusFilter;
      }
      if (categoryFilter && categoryFilter !== 'all') {
        params.category = categoryFilter;
      }
      if (priorityFilter && priorityFilter !== 'all') {
        params.priority = priorityFilter;
      }
      if (search) {
        params.search = search;
      }
      
      const [vosRes, statsRes] = await Promise.all([
        variationOrdersApi.list(params),
        variationOrdersApi.getStats(projectFilter !== 'all' ? projectFilter : null)
      ]);
      
      setVariationOrders(vosRes.data.variation_orders || []);
      setTotalPages(Math.ceil((vosRes.data.total || 0) / pageSize));
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to load VOs:', error);
      toast.error('Failed to load Variation Orders');
    } finally {
      setLoading(false);
    }
  }, [page, projectFilter, statusFilter, categoryFilter, priorityFilter, search]);

  // Load dashboard analytics
  const loadAnalytics = useCallback(async () => {
    setAnalyticsLoading(true);
    try {
      const res = await variationOrdersApi.getDashboardAnalytics(
        projectFilter !== 'all' ? projectFilter : null
      );
      setAnalytics(res.data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
    } finally {
      setAnalyticsLoading(false);
    }
  }, [projectFilter]);

  useEffect(() => {
    loadVariationOrders();
  }, [loadVariationOrders]);

  // Load analytics when switching to dashboard/reports tab
  useEffect(() => {
    if (activeTab === 'dashboard' || activeTab === 'reports') {
      loadAnalytics();
    }
  }, [activeTab, loadAnalytics]);

  // Create VO
  const handleCreateVO = async () => {
    if (!newVO.project_id) {
      toast.error('Please select a project');
      return;
    }
    if (!newVO.title) {
      toast.error('Please enter a title');
      return;
    }
    if (!newVO.change_category) {
      toast.error('Please select a change category');
      return;
    }
    
    setCreating(true);
    try {
      const res = await variationOrdersApi.create(newVO);
      toast.success(`Variation Order ${res.data.variation_order.vo_number} created`);
      setCreateDialogOpen(false);
      setNewVO({
        project_id: projectIdFromUrl || '',
        title: '',
        description: '',
        change_category: '',
        contract_type: 'JBCC',
        priority: 'Normal',
        disciplines: [],
        reason: '',
        proceed_at_risk: false,
      });
      loadVariationOrders();
      
      // Navigate to the new VO
      navigate(`/variation-orders/${res.data.variation_order.id}`);
    } catch (error) {
      console.error('Failed to create VO:', error);
      toast.error(error.response?.data?.detail || 'Failed to create Variation Order');
    } finally {
      setCreating(false);
    }
  };

  // Clear filters
  const clearFilters = () => {
    setSearch('');
    setProjectFilter('all');
    setStatusFilter('all');
    setCategoryFilter('all');
    setPriorityFilter('all');
    setPage(1);
  };

  const hasActiveFilters = search || projectFilter !== 'all' || statusFilter !== 'all' || 
                           categoryFilter !== 'all' || priorityFilter !== 'all';

  return (
    <DashboardLayout>
      <div className="space-y-6 overflow-x-hidden" data-testid="variation-orders-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="min-w-0">
            {isProjectSpecific && (
              <Button
                onClick={() => navigate(`/projects/${projectIdFromUrl}/dashboard`)}
                variant="ghost"
                className="mb-2 -ml-2 text-slate-600"
              >
                ← Back to Project
              </Button>
            )}
            <h1 className="text-xl sm:text-3xl md:text-4xl font-barlow font-bold uppercase tracking-tight">
              Variation Orders
            </h1>
            <p className="text-sm sm:text-base text-slate-600 mt-1">
              {isProjectSpecific 
                ? `Project variation orders and approvals`
                : 'Manage project changes, pricing, and approvals'
              }
            </p>
          </div>
          <Button
            onClick={() => setCreateDialogOpen(true)}
            className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm font-medium uppercase tracking-wide text-xs px-4 sm:px-6 py-3 shrink-0"
            data-testid="create-vo-button"
          >
            <Plus className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">New Variation Order</span>
            <span className="sm:hidden">New VO</span>
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            <Card className="rounded-sm border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <FileText className="h-4 w-4 text-slate-500" />
                  <span className="text-xs font-medium text-slate-500 uppercase">Total</span>
                </div>
                <p className="text-2xl font-bold">{stats.total_vos}</p>
              </CardContent>
            </Card>
            <Card className="rounded-sm border-amber-200 bg-amber-50/30">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="h-4 w-4 text-amber-600" />
                  <span className="text-xs font-medium text-amber-600 uppercase">Pending</span>
                </div>
                <p className="text-2xl font-bold text-amber-700">
                  {formatCurrency(stats.total_pending)}
                </p>
              </CardContent>
            </Card>
            <Card className="rounded-sm border-green-200 bg-green-50/30">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="text-xs font-medium text-green-600 uppercase">Approved</span>
                </div>
                <p className="text-2xl font-bold text-green-700">
                  {formatCurrency(stats.total_approved)}
                </p>
              </CardContent>
            </Card>
            <Card className="rounded-sm border-blue-200 bg-blue-50/30">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <DollarSign className="h-4 w-4 text-blue-600" />
                  <span className="text-xs font-medium text-blue-600 uppercase">Quoted</span>
                </div>
                <p className="text-2xl font-bold text-blue-700">
                  {formatCurrency(stats.total_quoted)}
                </p>
              </CardContent>
            </Card>
            <Card className="rounded-sm border-purple-200 bg-purple-50/30">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Calendar className="h-4 w-4 text-purple-600" />
                  <span className="text-xs font-medium text-purple-600 uppercase">Time Impact</span>
                </div>
                <p className="text-2xl font-bold text-purple-700">{stats.time_impact_days} days</p>
              </CardContent>
            </Card>
            <Card className="rounded-sm border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <TrendingUp className="h-4 w-4 text-slate-500" />
                  <span className="text-xs font-medium text-slate-500 uppercase">By Status</span>
                </div>
                <div className="flex flex-wrap gap-1 mt-1">
                  {Object.entries(stats.by_status || {}).slice(0, 3).map(([status, count]) => (
                    <Badge key={status} variant="secondary" className="text-xs">
                      {status}: {count}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Main Tabs: Register, Dashboard, Reports */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full max-w-md grid-cols-3">
            <TabsTrigger value="register" className="flex items-center gap-2">
              <List className="h-4 w-4" />
              Register
            </TabsTrigger>
            <TabsTrigger value="dashboard" className="flex items-center gap-2">
              <LayoutDashboard className="h-4 w-4" />
              Dashboard
            </TabsTrigger>
            <TabsTrigger value="reports" className="flex items-center gap-2">
              <FileSpreadsheet className="h-4 w-4" />
              Reports
            </TabsTrigger>
          </TabsList>

          {/* Register Tab */}
          <TabsContent value="register" className="mt-6 space-y-4">
            {/* Filters */}
            <div className="flex flex-wrap gap-3 items-center">
              <div className="relative flex-1 min-w-[200px] max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search VOs..."
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  className="pl-9 rounded-sm"
                  data-testid="vo-search"
                />
              </div>
              
              <Select value={projectFilter} onValueChange={(v) => { setProjectFilter(v); setPage(1); }}>
                <SelectTrigger className="w-48 rounded-sm" data-testid="project-filter">
                  <SelectValue placeholder="All Projects" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Projects</SelectItem>
                  {projects.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
                <SelectTrigger className="w-40 rounded-sm" data-testid="status-filter">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  {enums?.statuses?.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={categoryFilter} onValueChange={(v) => { setCategoryFilter(v); setPage(1); }}>
                <SelectTrigger className="w-44 rounded-sm" data-testid="category-filter">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {enums?.change_categories?.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={priorityFilter} onValueChange={(v) => { setPriorityFilter(v); setPage(1); }}>
                <SelectTrigger className="w-32 rounded-sm" data-testid="priority-filter">
                  <SelectValue placeholder="Priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priority</SelectItem>
                  {enums?.priorities?.map((p) => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  <X className="h-4 w-4 mr-1" /> Clear
                </Button>
              )}
              
              <Button variant="outline" size="sm" onClick={loadVariationOrders} className="ml-auto">
                <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>

            {/* VO List */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
              </div>
            ) : variationOrders.length === 0 ? (
              <Card className="rounded-sm">
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <FileText className="h-12 w-12 text-slate-300 mb-4" />
                  <h3 className="text-lg font-semibold text-slate-700 mb-2">No Variation Orders</h3>
                  <p className="text-slate-500 mb-4">
                    {hasActiveFilters ? 'No VOs match your filters' : 'Create your first Variation Order to get started'}
                  </p>
                  {hasActiveFilters ? (
                    <Button variant="outline" onClick={clearFilters}>Clear Filters</Button>
                  ) : (
                    <Button onClick={() => setCreateDialogOpen(true)}>
                      <Plus className="h-4 w-4 mr-2" /> Create Variation Order
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {variationOrders.map((vo) => (
                  <Card 
                    key={vo.id} 
                    className="rounded-sm hover:shadow-md transition-shadow cursor-pointer group"
                    onClick={() => navigate(`/variation-orders/${vo.id}`)}
                    data-testid={`vo-card-${vo.vo_number}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <span className="font-mono text-sm font-bold text-slate-900">
                              {vo.vo_number}
                            </span>
                            <Badge className={`${getStatusBadge(vo.status)} border text-xs`}>
                              {vo.status}
                            </Badge>
                            <Badge className={`${getPriorityBadge(vo.priority)} text-xs`}>
                              {vo.priority}
                            </Badge>
                            {vo.proceed_at_risk && (
                              <Badge className="bg-red-100 text-red-700 border-red-200 text-xs">
                                At Risk
                              </Badge>
                            )}
                          </div>
                          <h3 className="font-semibold text-slate-800 group-hover:text-blue-600 transition-colors truncate">
                            {vo.title}
                          </h3>
                          <p className="text-sm text-slate-600 line-clamp-1 mt-1">
                            {vo.description}
                          </p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                            <span className="flex items-center gap-1">
                              <Building2 className="h-3 w-3" />
                              {projects.find(p => p.id === vo.project_id)?.name || 'Unknown Project'}
                            </span>
                            <span>{vo.change_category}</span>
                            <span className="font-mono">
                              {new Date(vo.created_at).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          {vo.approved_total ? (
                            <div>
                              <p className="text-xs text-slate-500 uppercase">Approved</p>
                              <p className="font-bold text-green-600">{formatCurrency(vo.approved_total)}</p>
                            </div>
                          ) : vo.assessed_total ? (
                            <div>
                              <p className="text-xs text-slate-500 uppercase">Assessed</p>
                              <p className="font-bold text-blue-600">{formatCurrency(vo.assessed_total)}</p>
                            </div>
                          ) : vo.quoted_total ? (
                            <div>
                              <p className="text-xs text-slate-500 uppercase">Quoted</p>
                              <p className="font-bold text-amber-600">{formatCurrency(vo.quoted_total)}</p>
                            </div>
                          ) : (
                            <p className="text-sm text-slate-400">No value</p>
                          )}
                          <ArrowRight className="h-4 w-4 text-slate-400 group-hover:text-blue-600 transition-colors mt-2 ml-auto" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <span className="flex items-center px-3 text-sm text-slate-600">
                  Page {page} of {totalPages}
                </span>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  Next
                </Button>
              </div>
            )}
          </TabsContent>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="mt-6 space-y-6">
            {analyticsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
              </div>
            ) : analytics ? (
              <>
                {/* Status Pipeline */}
                <Card className="rounded-sm">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <BarChart3 className="h-5 w-5" />
                      VO Status Pipeline
                    </CardTitle>
                    <CardDescription>Current distribution across workflow stages</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                      {Object.entries(analytics.status_pipeline || {}).map(([stage, count]) => (
                        <div key={stage} className="text-center p-3 bg-slate-50 rounded-lg border">
                          <p className="text-2xl font-bold">{count}</p>
                          <p className="text-xs text-slate-500 mt-1">{stage}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Cost by Category Chart */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Card className="rounded-sm">
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <PieChart className="h-5 w-5" />
                        Cost by Category
                      </CardTitle>
                      <CardDescription>Distribution of VO values by change category</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3 max-h-[300px] overflow-y-auto">
                        {Object.entries(analytics.cost_by_category || {})
                          .sort((a, b) => (b[1].quoted + b[1].approved) - (a[1].quoted + a[1].approved))
                          .map(([category, data]) => {
                            const maxValue = Math.max(...Object.values(analytics.cost_by_category || {}).map(d => d.quoted + d.approved));
                            const percent = maxValue > 0 ? ((data.quoted + data.approved) / maxValue) * 100 : 0;
                            return (
                              <div key={category} className="space-y-1">
                                <div className="flex justify-between text-sm">
                                  <span className="truncate">{category}</span>
                                  <span className="font-mono font-medium">{formatCurrency(data.approved || data.quoted)}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Progress value={percent} className="h-2 flex-1" />
                                  <span className="text-xs text-slate-500 w-8">{data.count}</span>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Monthly Trend */}
                  <Card className="rounded-sm">
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <TrendingUp className="h-5 w-5" />
                        Monthly Trend
                      </CardTitle>
                      <CardDescription>VOs created and value over the last 6 months</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {Object.entries(analytics.monthly_trend || {}).map(([month, data]) => {
                          const maxValue = Math.max(...Object.values(analytics.monthly_trend || {}).map(d => d.value));
                          const percent = maxValue > 0 ? (data.value / maxValue) * 100 : 0;
                          return (
                            <div key={month} className="space-y-1">
                              <div className="flex justify-between text-sm">
                                <span className="font-mono">{month}</span>
                                <span className="text-slate-500">{data.count} VOs • {formatCurrency(data.value)}</span>
                              </div>
                              <Progress value={percent} className="h-2" />
                            </div>
                          );
                        })}
                        {Object.keys(analytics.monthly_trend || {}).length === 0 && (
                          <p className="text-center text-slate-500 py-6">No monthly data available</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Top VOs and Urgent VOs */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Card className="rounded-sm">
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <DollarSign className="h-5 w-5" />
                        Top VOs by Value
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 max-h-[300px] overflow-y-auto">
                        {(analytics.top_vos_by_value || []).map((vo, idx) => (
                          <div 
                            key={vo.id}
                            className="flex items-center justify-between p-2 hover:bg-slate-50 rounded cursor-pointer"
                            onClick={() => navigate(`/variation-orders/${vo.id}`)}
                          >
                            <div className="flex items-center gap-2">
                              <span className="w-6 h-6 flex items-center justify-center bg-slate-100 rounded-full text-xs font-bold">
                                {idx + 1}
                              </span>
                              <div>
                                <p className="text-sm font-medium">{vo.vo_number}</p>
                                <p className="text-xs text-slate-500 truncate max-w-[200px]">{vo.title}</p>
                              </div>
                            </div>
                            <span className="font-mono font-bold text-green-600">{formatCurrency(vo.value)}</span>
                          </div>
                        ))}
                        {(analytics.top_vos_by_value || []).length === 0 && (
                          <p className="text-center text-slate-500 py-6">No VOs with values</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="rounded-sm">
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5 text-amber-500" />
                        Urgent & Emergency VOs
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 max-h-[300px] overflow-y-auto">
                        {(analytics.urgent_vos || []).map((vo) => (
                          <div 
                            key={vo.id}
                            className="flex items-center justify-between p-2 hover:bg-slate-50 rounded cursor-pointer"
                            onClick={() => navigate(`/variation-orders/${vo.id}`)}
                          >
                            <div className="flex items-center gap-2">
                              <Badge className={vo.priority === 'Emergency' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}>
                                {vo.priority}
                              </Badge>
                              <div>
                                <p className="text-sm font-medium">{vo.vo_number}</p>
                                <p className="text-xs text-slate-500">{vo.status} • {vo.age_days}d old</p>
                              </div>
                            </div>
                            <span className="font-mono text-sm">{formatCurrency(vo.value)}</span>
                          </div>
                        ))}
                        {(analytics.urgent_vos || []).length === 0 && (
                          <div className="text-center py-6">
                            <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
                            <p className="text-slate-500">No urgent VOs</p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </>
            ) : (
              <Card className="rounded-sm">
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <BarChart3 className="h-12 w-12 text-slate-300 mb-4" />
                  <p className="text-slate-500">No analytics data available</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Reports Tab - Ageing Report */}
          <TabsContent value="reports" className="mt-6 space-y-6">
            {analyticsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
              </div>
            ) : analytics ? (
              <>
                {/* Ageing Report */}
                <Card className="rounded-sm">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Timer className="h-5 w-5" />
                        VO Ageing Report
                      </CardTitle>
                      <CardDescription>Pending VOs grouped by age (days since creation)</CardDescription>
                    </div>
                    <Button variant="outline" size="sm" className="gap-2">
                      <Download className="h-4 w-4" />
                      Export CSV
                    </Button>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                      {Object.entries(analytics.ageing_report || {}).map(([bucket, data]) => {
                        const isOverdue = bucket.includes('30') || bucket.includes('60') || bucket.includes('90');
                        return (
                          <div 
                            key={bucket} 
                            className={`p-4 rounded-lg border ${isOverdue ? 'bg-red-50 border-red-200' : 'bg-slate-50'}`}
                          >
                            <p className={`text-xs font-medium uppercase ${isOverdue ? 'text-red-600' : 'text-slate-500'}`}>
                              {bucket.replace(/_/g, ' ')}
                            </p>
                            <p className={`text-2xl font-bold mt-1 ${isOverdue ? 'text-red-700' : ''}`}>{data.count}</p>
                            <p className={`text-sm font-mono mt-1 ${isOverdue ? 'text-red-600' : 'text-slate-600'}`}>
                              {formatCurrency(data.value)}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                {/* Overdue VOs Table */}
                <Card className="rounded-sm">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <AlertCircle className="h-5 w-5 text-red-500" />
                      Overdue VOs (30+ Days Pending)
                      <Badge variant="destructive" className="ml-2">{(analytics.overdue_vos || []).length}</Badge>
                    </CardTitle>
                    <CardDescription>VOs that have been pending for more than 30 days require attention</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {(analytics.overdue_vos || []).length > 0 ? (
                      <div className="border rounded-lg overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-50">
                            <tr>
                              <th className="text-left p-3 font-medium">VO #</th>
                              <th className="text-left p-3 font-medium">Title</th>
                              <th className="text-left p-3 font-medium">Status</th>
                              <th className="text-right p-3 font-medium">Age</th>
                              <th className="text-right p-3 font-medium">Value</th>
                              <th className="text-center p-3 font-medium">Action</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {(analytics.overdue_vos || []).map((vo) => (
                              <tr key={vo.id} className="hover:bg-slate-50">
                                <td className="p-3 font-mono font-bold">{vo.vo_number}</td>
                                <td className="p-3 truncate max-w-[200px]">{vo.title}</td>
                                <td className="p-3">
                                  <Badge className={getStatusBadge(vo.status)}>{vo.status}</Badge>
                                </td>
                                <td className="p-3 text-right">
                                  <span className={`font-bold ${vo.age_days > 60 ? 'text-red-600' : 'text-amber-600'}`}>
                                    {vo.age_days}d
                                  </span>
                                </td>
                                <td className="p-3 text-right font-mono">{formatCurrency(vo.value)}</td>
                                <td className="p-3 text-center">
                                  <Button 
                                    size="sm" 
                                    variant="ghost"
                                    onClick={() => navigate(`/variation-orders/${vo.id}`)}
                                  >
                                    <ArrowUpRight className="h-4 w-4" />
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
                        <p className="text-lg font-medium text-slate-700">All VOs are up to date!</p>
                        <p className="text-slate-500">No VOs have been pending for more than 30 days</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Cost Impact Summary by Status */}
                <Card className="rounded-sm">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <DollarSign className="h-5 w-5" />
                      Cost Impact by Status
                    </CardTitle>
                    <CardDescription>Financial distribution across workflow stages</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="text-left p-3 font-medium">Status</th>
                            <th className="text-right p-3 font-medium">Count</th>
                            <th className="text-right p-3 font-medium">Total Value</th>
                            <th className="text-right p-3 font-medium">% of Total</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {Object.entries(analytics.cost_by_status || {})
                            .sort((a, b) => b[1].value - a[1].value)
                            .map(([status, data]) => {
                              const totalValue = Object.values(analytics.cost_by_status || {}).reduce((sum, d) => sum + d.value, 0);
                              const percent = totalValue > 0 ? ((data.value / totalValue) * 100).toFixed(1) : 0;
                              return (
                                <tr key={status} className="hover:bg-slate-50">
                                  <td className="p-3">
                                    <Badge className={getStatusBadge(status)}>{status}</Badge>
                                  </td>
                                  <td className="p-3 text-right">{data.count}</td>
                                  <td className="p-3 text-right font-mono font-medium">{formatCurrency(data.value)}</td>
                                  <td className="p-3 text-right text-slate-500">{percent}%</td>
                                </tr>
                              );
                            })}
                        </tbody>
                        <tfoot className="bg-slate-100 font-bold">
                          <tr>
                            <td className="p-3">TOTAL</td>
                            <td className="p-3 text-right">
                              {Object.values(analytics.cost_by_status || {}).reduce((sum, d) => sum + d.count, 0)}
                            </td>
                            <td className="p-3 text-right font-mono">
                              {formatCurrency(Object.values(analytics.cost_by_status || {}).reduce((sum, d) => sum + d.value, 0))}
                            </td>
                            <td className="p-3 text-right">100%</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : (
              <Card className="rounded-sm">
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <FileSpreadsheet className="h-12 w-12 text-slate-300 mb-4" />
                  <p className="text-slate-500">No report data available</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {/* Create VO Dialog */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-barlow font-bold uppercase">
                Create Variation Order
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Project */}
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  Project *
                </Label>
                <Select 
                  value={newVO.project_id} 
                  onValueChange={(v) => setNewVO({ ...newVO, project_id: v })}
                >
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="new-vo-project">
                    <SelectValue placeholder="Select project" />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Title */}
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  Title *
                </Label>
                <Input
                  value={newVO.title}
                  onChange={(e) => setNewVO({ ...newVO, title: e.target.value })}
                  placeholder="Brief title for this variation"
                  className="mt-1 rounded-sm"
                  data-testid="new-vo-title"
                />
              </div>

              {/* Change Category & Contract Type */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    Change Category *
                  </Label>
                  <Select 
                    value={newVO.change_category} 
                    onValueChange={(v) => setNewVO({ ...newVO, change_category: v })}
                  >
                    <SelectTrigger className="mt-1 rounded-sm" data-testid="new-vo-category">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {enums?.change_categories?.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    Contract Type
                  </Label>
                  <Select 
                    value={newVO.contract_type} 
                    onValueChange={(v) => setNewVO({ ...newVO, contract_type: v })}
                  >
                    <SelectTrigger className="mt-1 rounded-sm" data-testid="new-vo-contract-type">
                      <SelectValue placeholder="Select contract type" />
                    </SelectTrigger>
                    <SelectContent>
                      {enums?.contract_types?.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Priority & Disciplines */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    Priority
                  </Label>
                  <Select 
                    value={newVO.priority} 
                    onValueChange={(v) => setNewVO({ ...newVO, priority: v })}
                  >
                    <SelectTrigger className="mt-1 rounded-sm" data-testid="new-vo-priority">
                      <SelectValue placeholder="Select priority" />
                    </SelectTrigger>
                    <SelectContent>
                      {enums?.priorities?.map((p) => (
                        <SelectItem key={p} value={p}>{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    Disciplines
                  </Label>
                  <Select 
                    value={newVO.disciplines[0] || ''} 
                    onValueChange={(v) => setNewVO({ ...newVO, disciplines: v ? [v] : [] })}
                  >
                    <SelectTrigger className="mt-1 rounded-sm" data-testid="new-vo-discipline">
                      <SelectValue placeholder="Select discipline" />
                    </SelectTrigger>
                    <SelectContent>
                      {enums?.disciplines?.map((d) => (
                        <SelectItem key={d} value={d}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Description */}
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  Description
                </Label>
                <Textarea
                  value={newVO.description}
                  onChange={(e) => setNewVO({ ...newVO, description: e.target.value })}
                  placeholder="Detailed description of the change..."
                  rows={4}
                  className="mt-1 rounded-sm"
                  data-testid="new-vo-description"
                />
              </div>

              {/* Reason */}
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  Reason / Trigger
                </Label>
                <Input
                  value={newVO.reason}
                  onChange={(e) => setNewVO({ ...newVO, reason: e.target.value })}
                  placeholder="Who requested this and why?"
                  className="mt-1 rounded-sm"
                  data-testid="new-vo-reason"
                />
              </div>

              {/* Proceed at Risk */}
              <div className="flex items-center space-x-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <Checkbox
                  id="proceed_at_risk"
                  checked={newVO.proceed_at_risk}
                  onCheckedChange={(checked) => setNewVO({ ...newVO, proceed_at_risk: checked })}
                />
                <div>
                  <label htmlFor="proceed_at_risk" className="text-sm font-medium text-amber-800 cursor-pointer">
                    Proceed at Risk
                  </label>
                  <p className="text-xs text-amber-600">
                    Work may begin before formal approval (contractor assumes risk)
                  </p>
                </div>
              </div>
            </div>

            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button variant="outline" onClick={() => setCreateDialogOpen(false)} className="w-full sm:w-auto">
                Cancel
              </Button>
              <Button 
                onClick={handleCreateVO} 
                disabled={creating}
                className="w-full sm:w-auto bg-slate-900 hover:bg-slate-800"
                data-testid="create-vo-submit"
              >
                {creating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
                Create Variation Order
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
